/**
 * InvoicePrintPreview — editor visual de plantillas de impresión de facturas.
 *
 * Plantillas disponibles: Formal A4 · Compacto · Térmico 80mm
 * Las preferencias se cargan desde la configuración del tenant (API) con fallback a localStorage.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PrinterIcon,
  ArrowLeftIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { invoicesAPI, configurationAPI } from '../services/api';
import { LoadingSpinner } from '../components/ui';
import InvoicePrintSettingsForm from '../components/invoice-print/InvoicePrintSettingsForm';
import {
  InvoicePrintSettings,
  DEFAULT_INVOICE_PRINT_SETTINGS,
  normalizeInvoicePrintSettings,
  loadInvoicePrintSettingsFromLocalStorage,
  saveInvoicePrintSettingsToLocalStorage,
} from '../lib/invoicePrintSettings';

type PrintSettings = InvoicePrintSettings;
const DEFAULT_SETTINGS = DEFAULT_INVOICE_PRINT_SETTINGS;
/* ─── Helpers ───────────────────────────────────────────── */
const fmt = (minor?: number, major?: number) => {
  const v = minor != null ? minor / 100 : Number(major || 0);
  return `$${v.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
};

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A';

const STATUS_ES: Record<string, string> = {
  DRAFT: 'Borrador', ISSUED: 'Emitida', PARTIAL: 'Pago parcial',
  PAID: 'Pagada', OVERDUE: 'Vencida', CANCELLED: 'Anulada', PENDING: 'Pendiente',
};

/* ═══════════════════════════════════════════════════════════
   TEMPLATE: FORMAL A4
═══════════════════════════════════════════════════════════ */
const TemplateFormal: React.FC<{ invoice: any; config: any; settings: PrintSettings }> = ({
  invoice, config, settings,
}) => {
  const col = settings.primaryColor;
  const client = invoice.client || {};
  const plan = invoice.clientPlan?.planTemplate;
  const allocations: any[] = invoice.paymentAllocations || [];
  const totalPaidMinor = allocations
    .filter((a: any) => a.payment?.status === 'CONFIRMED' || a.payment?.status === 'PARTIAL_REFUND')
    .reduce((s: number, a: any) => s + (a.amountAppliedMinor || 0), 0);

  return (
    <div className="invoice-page formal" style={{ fontFamily: 'Arial, sans-serif', color: '#111', background: '#fff' }}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .invoice-page, .invoice-page * { visibility: visible !important; }
          .invoice-page { position: absolute; top: 0; left: 0; width: 100%; }
          @page { size: A4; margin: 12mm; }
        }
        .invoice-page.formal { width: 794px; min-height: 1123px; padding: 40px; box-sizing: border-box; }
        .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
        .inv-company { flex: 1; }
        .inv-company h1 { font-size: 18px; font-weight: 700; margin: 0 0 4px; color: ${col}; }
        .inv-company p { font-size: 10px; color: #555; margin: 1px 0; }
        .inv-logo { max-height: 60px; max-width: 160px; object-fit: contain; }
        .inv-title-block { text-align: right; }
        .inv-title-block h2 { font-size: 24px; font-weight: 800; color: ${col}; margin: 0 0 6px; letter-spacing: 1px; }
        .inv-title-block p { font-size: 10px; color: #555; margin: 1px 0; }
        .copy-badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 8px; border: 1.5px solid ${col}; color: ${col}; border-radius: 3px; margin-top: 6px; }
        .divider { border: none; border-top: 2px solid ${col}; margin: 16px 0; }
        .divider-light { border: none; border-top: 1px solid #e5e7eb; margin: 12px 0; }
        .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
        .section-title { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: ${col}; margin: 0 0 6px; }
        .field-row { display: flex; justify-content: space-between; font-size: 10px; margin: 3px 0; }
        .field-label { color: #666; }
        .field-value { font-weight: 600; color: #111; text-align: right; }
        .status-badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 10px; background: ${col}20; color: ${col}; }
        table.det { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 16px; }
        table.det th { background: ${col}; color: #fff; padding: 6px 8px; text-align: left; font-size: 9px; letter-spacing: 0.5px; }
        table.det td { padding: 6px 8px; border-bottom: 1px solid #f0f0f0; }
        table.det tr:nth-child(even) td { background: #f9fafb; }
        .totals-block { margin-left: auto; width: 260px; }
        .total-row { display: flex; justify-content: space-between; font-size: 10px; padding: 3px 0; }
        .total-row.grand { font-size: 13px; font-weight: 700; color: ${col}; border-top: 1.5px solid ${col}; padding-top: 6px; margin-top: 4px; }
        .signature-row { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 40px; }
        .sig-line { border-top: 1px solid #999; padding-top: 6px; font-size: 9px; color: #666; text-align: center; }
        .footer-text { font-size: 9px; color: #888; text-align: center; margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
      `}</style>

      {/* Header */}
      <div className="inv-header">
        <div className="inv-company">
          {settings.showLogo && config?.logoUrl && (
            <img src={config.logoUrl} alt="logo" className="inv-logo" style={{ marginBottom: 8 }} />
          )}
          <h1>{config?.companyName || 'Funeraria'}</h1>
          <p>{config?.address || ''}</p>
          <p>Tel: {config?.phone || ''}</p>
          <p>{config?.email || ''}</p>
          {settings.showRUC && config?.ruc && <p>RNC/RUC: {config.ruc}</p>}
        </div>
        <div className="inv-title-block">
          <h2>FACTURA</h2>
          <p>N° {invoice.invoiceNumber || `#${invoice.id}`}</p>
          <p>Fecha: {fmtDate(invoice.createdAt)}</p>
          <p>Vencimiento: {fmtDate(invoice.dueDate)}</p>
          <div><span className="status-badge">{STATUS_ES[invoice.status] || invoice.status}</span></div>
          <div><span className="copy-badge">{settings.copyLabel}</span></div>
        </div>
      </div>

      <hr className="divider" />

      {/* Client + Plan */}
      <div className="two-col">
        <div>
          <p className="section-title">Datos del cliente</p>
          <div className="field-row"><span className="field-label">Nombre</span><span className="field-value">{client.name} {client.lastname || ''}</span></div>
          <div className="field-row"><span className="field-label">Cédula</span><span className="field-value">{client.cedula || '—'}</span></div>
          <div className="field-row"><span className="field-label">Gremio</span><span className="field-value">{client.gremio || '—'}</span></div>
          {client.address && <div className="field-row"><span className="field-label">Dirección</span><span className="field-value">{client.address}</span></div>}
        </div>
        <div>
          <p className="section-title">Plan contratado</p>
          <div className="field-row"><span className="field-label">Plan</span><span className="field-value">{plan?.name || '—'}</span></div>
          <div className="field-row"><span className="field-label">Tipo</span><span className="field-value">{plan?.planType || '—'}</span></div>
          <div className="field-row"><span className="field-label">Precio base</span><span className="field-value">{fmt(undefined, plan?.basePrice)}</span></div>
          <div className="field-row"><span className="field-label">Precio final</span><span className="field-value">{fmt(undefined, invoice.clientPlan?.finalPrice)}</span></div>
        </div>
      </div>

      <hr className="divider-light" />

      {/* Detail table */}
      <p className="section-title" style={{ marginBottom: 8 }}>Detalle del servicio</p>
      <table className="det">
        <thead>
          <tr>
            <th>Descripción</th>
            <th>Tipo</th>
            <th style={{ textAlign: 'right' }}>Subtotal</th>
            <th style={{ textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{plan?.name || 'Servicio funerario'}</td>
            <td>{plan?.planType || '—'}</td>
            <td style={{ textAlign: 'right' }}>{fmt(invoice.subtotalMinor, invoice.amountDue)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(invoice.totalMinor, invoice.amountDue)}</td>
          </tr>
        </tbody>
      </table>

      {/* Totals */}
      <div className="totals-block">
        {(invoice.taxMinor || 0) > 0 && (
          <div className="total-row"><span>Impuesto</span><span>{fmt(invoice.taxMinor)}</span></div>
        )}
        {(invoice.moraMinor || 0) > 0 && (
          <div className="total-row"><span>Mora / recargo</span><span>{fmt(invoice.moraMinor)}</span></div>
        )}
        <div className="total-row grand"><span>TOTAL</span><span>{fmt(invoice.totalMinor, invoice.amountDue)}</span></div>
        {settings.showPaymentHistory && totalPaidMinor > 0 && (
          <div className="total-row" style={{ color: '#059669' }}><span>Pagado</span><span>-{fmt(totalPaidMinor)}</span></div>
        )}
        <div className="total-row" style={{ fontWeight: 600 }}>
          <span>Saldo pendiente</span>
          <span>{fmt(invoice.balanceDueMinor)}</span>
        </div>
      </div>

      {/* Payment history */}
      {settings.showPaymentHistory && allocations.length > 0 && (
        <>
          <hr className="divider-light" />
          <p className="section-title" style={{ marginBottom: 8 }}>Historial de pagos</p>
          <table className="det">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Método</th>
                <th>Referencia</th>
                <th style={{ textAlign: 'right' }}>Aplicado</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a: any) => (
                <tr key={a.id}>
                  <td>{fmtDate(a.payment?.paymentDate)}</td>
                  <td>{a.payment?.paymentMethod || '—'}</td>
                  <td>{a.payment?.reference || a.payment?.bankReference || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(a.amountAppliedMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Notes */}
      {settings.showNotes && invoice.notes && (
        <>
          <hr className="divider-light" />
          <p className="section-title">Notas</p>
          <p style={{ fontSize: 10, color: '#555' }}>{invoice.notes}</p>
        </>
      )}

      {/* Signature */}
      {settings.showSignatureLine && (
        <div className="signature-row">
          <div className="sig-line">Firma del cliente</div>
          <div className="sig-line">Autorizado por</div>
        </div>
      )}

      {/* Footer */}
      <div className="footer-text">
        {settings.footerText}
        {config?.ruc && settings.showRUC && <span> · RNC/RUC: {config.ruc}</span>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TEMPLATE: COMPACT
═══════════════════════════════════════════════════════════ */
const TemplateCompact: React.FC<{ invoice: any; config: any; settings: PrintSettings }> = ({
  invoice, config, settings,
}) => {
  const col = settings.primaryColor;
  const client = invoice.client || {};
  const plan = invoice.clientPlan?.planTemplate;
  const allocations: any[] = invoice.paymentAllocations || [];
  const totalPaidMinor = allocations.reduce((s: number, a: any) => s + (a.amountAppliedMinor || 0), 0);

  return (
    <div className="invoice-page compact" style={{ fontFamily: 'Arial, sans-serif', color: '#111', background: '#fff' }}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .invoice-page, .invoice-page * { visibility: visible !important; }
          .invoice-page { position: absolute; top: 0; left: 0; width: 100%; }
          @page { size: A4; margin: 10mm; }
        }
        .invoice-page.compact { width: 794px; padding: 24px; box-sizing: border-box; }
        .cp-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 2px solid ${col}; }
        .cp-header h1 { font-size: 14px; font-weight: 700; color: ${col}; margin: 0 0 2px; }
        .cp-header p { font-size: 9px; color: #666; margin: 0; }
        .cp-title { text-align: right; }
        .cp-title h2 { font-size: 20px; font-weight: 800; color: ${col}; margin: 0; }
        .cp-title p { font-size: 9px; color: #555; margin: 1px 0; }
        .cp-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin: 10px 0; font-size: 9px; }
        .cp-section h4 { font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: ${col}; margin: 0 0 4px; }
        .cp-section p { margin: 1px 0; color: #333; }
        table.cp-det { width: 100%; border-collapse: collapse; font-size: 9px; margin: 10px 0; }
        table.cp-det th { background: ${col}; color: #fff; padding: 4px 6px; text-align: left; }
        table.cp-det td { padding: 4px 6px; border-bottom: 1px solid #f0f0f0; }
        .cp-totals { display: flex; justify-content: flex-end; margin-top: 6px; }
        .cp-totals table { font-size: 9px; border-collapse: collapse; }
        .cp-totals td { padding: 2px 6px; }
        .cp-totals .grand td { font-weight: 700; color: ${col}; font-size: 11px; border-top: 1.5px solid ${col}; }
        .cp-footer { font-size: 8px; color: #999; text-align: center; margin-top: 16px; border-top: 1px solid #e5e7eb; padding-top: 8px; }
        .cp-sig { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 24px; }
        .cp-sig-line { border-top: 1px solid #999; padding-top: 4px; font-size: 8px; color: #666; text-align: center; }
      `}</style>

      <div className="cp-header">
        <div>
          {settings.showLogo && config?.logoUrl && (
            <img src={config.logoUrl} alt="" style={{ height: 36, marginBottom: 4, display: 'block' }} />
          )}
          <h1>{config?.companyName || 'Funeraria'}</h1>
          <p>{config?.address} · {config?.phone}</p>
          {settings.showRUC && config?.ruc && <p>RNC/RUC: {config.ruc}</p>}
        </div>
        <div className="cp-title">
          <h2>FACTURA</h2>
          <p>N° {invoice.invoiceNumber || `#${invoice.id}`}</p>
          <p>Fecha: {fmtDate(invoice.createdAt)} · Vence: {fmtDate(invoice.dueDate)}</p>
          <p style={{ color: col, fontWeight: 700 }}>{STATUS_ES[invoice.status] || invoice.status} · {settings.copyLabel}</p>
        </div>
      </div>

      <div className="cp-grid">
        <div className="cp-section">
          <h4>Cliente</h4>
          <p><b>{client.name} {client.lastname || ''}</b></p>
          <p>Cédula: {client.cedula || '—'}</p>
          <p>Gremio: {client.gremio || '—'}</p>
        </div>
        <div className="cp-section">
          <h4>Plan contratado</h4>
          <p><b>{plan?.name || '—'}</b></p>
          <p>Tipo: {plan?.planType || '—'}</p>
          <p>Precio base: {fmt(undefined, plan?.basePrice)}</p>
        </div>
        <div className="cp-section">
          <h4>Resumen financiero</h4>
          <p>Total: <b>{fmt(invoice.totalMinor, invoice.amountDue)}</b></p>
          {totalPaidMinor > 0 && <p>Pagado: {fmt(totalPaidMinor)}</p>}
          <p>Saldo: <b style={{ color: invoice.balanceDueMinor === 0 ? '#059669' : col }}>{fmt(invoice.balanceDueMinor)}</b></p>
        </div>
      </div>

      <table className="cp-det">
        <thead><tr><th>Descripción</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Precio base</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
        <tbody>
          <tr>
            <td>{plan?.name || 'Servicio funerario'}</td>
            <td>{plan?.planType || '—'}</td>
            <td style={{ textAlign: 'right' }}>{fmt(undefined, plan?.basePrice)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(invoice.totalMinor, invoice.amountDue)}</td>
          </tr>
        </tbody>
      </table>

      <div className="cp-totals">
        <table>
          <tbody>
            {(invoice.taxMinor || 0) > 0 && <tr><td>Impuesto</td><td>{fmt(invoice.taxMinor)}</td></tr>}
            {(invoice.moraMinor || 0) > 0 && <tr><td>Mora</td><td>{fmt(invoice.moraMinor)}</td></tr>}
            {settings.showPaymentHistory && totalPaidMinor > 0 && <tr><td style={{ color: '#059669' }}>Pagado</td><td style={{ color: '#059669' }}>-{fmt(totalPaidMinor)}</td></tr>}
            <tr className="grand"><td>SALDO PENDIENTE</td><td>{fmt(invoice.balanceDueMinor)}</td></tr>
          </tbody>
        </table>
      </div>

      {settings.showPaymentHistory && allocations.length > 0 && (
        <table className="cp-det" style={{ marginTop: 12 }}>
          <thead><tr><th>Fecha pago</th><th>Método</th><th>Ref.</th><th style={{ textAlign: 'right' }}>Monto</th></tr></thead>
          <tbody>
            {allocations.map((a: any) => (
              <tr key={a.id}>
                <td>{fmtDate(a.payment?.paymentDate)}</td>
                <td>{a.payment?.paymentMethod || '—'}</td>
                <td>{a.payment?.reference || a.payment?.bankReference || '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmt(a.amountAppliedMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {settings.showNotes && invoice.notes && (
        <p style={{ fontSize: 9, color: '#555', marginTop: 8 }}><b>Notas:</b> {invoice.notes}</p>
      )}

      {settings.showSignatureLine && (
        <div className="cp-sig">
          <div className="cp-sig-line">Firma del cliente</div>
          <div className="cp-sig-line">Autorizado por</div>
        </div>
      )}

      <div className="cp-footer">{settings.footerText}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TEMPLATE: THERMAL 80mm
═══════════════════════════════════════════════════════════ */
const TemplateThermal: React.FC<{ invoice: any; config: any; settings: PrintSettings }> = ({
  invoice, config, settings,
}) => {
  const client = invoice.client || {};
  const plan = invoice.clientPlan?.planTemplate;
  const allocations: any[] = invoice.paymentAllocations || [];
  const totalPaidMinor = allocations.reduce((s: number, a: any) => s + (a.amountAppliedMinor || 0), 0);

  return (
    <div className="invoice-page thermal" style={{ fontFamily: 'monospace', color: '#000', background: '#fff' }}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .invoice-page, .invoice-page * { visibility: visible !important; }
          .invoice-page { position: absolute; top: 0; left: 0; }
          @page { size: 80mm auto; margin: 3mm; }
        }
        .invoice-page.thermal { width: 280px; padding: 8px; box-sizing: border-box; font-size: 10px; line-height: 1.5; }
        .th-center { text-align: center; }
        .th-bold { font-weight: 700; }
        .th-divider { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        .th-row { display: flex; justify-content: space-between; }
        .th-label { color: #333; }
        table.th-det { width: 100%; border-collapse: collapse; font-size: 9px; margin: 4px 0; }
        table.th-det td { padding: 2px 0; vertical-align: top; }
        table.th-det td:last-child { text-align: right; }
        .th-total-line { display: flex; justify-content: space-between; font-weight: 700; font-size: 11px; border-top: 1px solid #000; padding-top: 3px; margin-top: 3px; }
      `}</style>

      {/* Header */}
      <div className="th-center">
        <div className="th-bold" style={{ fontSize: 12 }}>{config?.companyName || 'FUNERARIA'}</div>
        {config?.address && <div style={{ fontSize: 9 }}>{config.address}</div>}
        {config?.phone && <div style={{ fontSize: 9 }}>Tel: {config.phone}</div>}
        {settings.showRUC && config?.ruc && <div style={{ fontSize: 9 }}>RNC: {config.ruc}</div>}
      </div>

      <hr className="th-divider" />

      <div className="th-center th-bold" style={{ fontSize: 11 }}>FACTURA</div>
      <div className="th-center" style={{ fontSize: 9 }}>{settings.copyLabel}</div>
      <div style={{ fontSize: 9, marginTop: 4 }}>
        <div className="th-row"><span>N°</span><span>{invoice.invoiceNumber || `#${invoice.id}`}</span></div>
        <div className="th-row"><span>Fecha</span><span>{fmtDate(invoice.createdAt)}</span></div>
        <div className="th-row"><span>Vence</span><span>{fmtDate(invoice.dueDate)}</span></div>
        <div className="th-row"><span>Estado</span><span className="th-bold">{STATUS_ES[invoice.status] || invoice.status}</span></div>
      </div>

      <hr className="th-divider" />

      <div style={{ fontSize: 9 }}>
        <div className="th-bold">CLIENTE</div>
        <div>{client.name} {client.lastname || ''}</div>
        {client.cedula && <div>Ced: {client.cedula}</div>}
        {client.gremio && <div>Gremio: {client.gremio}</div>}
      </div>

      <hr className="th-divider" />

      <div style={{ fontSize: 9 }}>
        <div className="th-bold">DETALLE</div>
        <table className="th-det">
          <tbody>
            <tr>
              <td>{plan?.name || 'Servicio funerario'}</td>
              <td>{fmt(invoice.totalMinor, invoice.amountDue)}</td>
            </tr>
            {plan?.planType && <tr><td style={{ paddingLeft: 8, color: '#555' }}>Tipo: {plan.planType}</td><td></td></tr>}
          </tbody>
        </table>
      </div>

      <hr className="th-divider" />

      <div style={{ fontSize: 9 }}>
        {(invoice.taxMinor || 0) > 0 && (
          <div className="th-row"><span>Impuesto</span><span>{fmt(invoice.taxMinor)}</span></div>
        )}
        {(invoice.moraMinor || 0) > 0 && (
          <div className="th-row"><span>Mora</span><span>{fmt(invoice.moraMinor)}</span></div>
        )}
        {settings.showPaymentHistory && totalPaidMinor > 0 && (
          <div className="th-row"><span>Pagado</span><span>-{fmt(totalPaidMinor)}</span></div>
        )}
      </div>
      <div className="th-total-line">
        <span>SALDO</span>
        <span>{fmt(invoice.balanceDueMinor)}</span>
      </div>

      {settings.showPaymentHistory && allocations.length > 0 && (
        <>
          <hr className="th-divider" />
          <div style={{ fontSize: 9 }}>
            <div className="th-bold">PAGOS</div>
            {allocations.map((a: any) => (
              <div key={a.id} className="th-row">
                <span>{fmtDate(a.payment?.paymentDate)} {a.payment?.paymentMethod?.slice(0, 6)}</span>
                <span>{fmt(a.amountAppliedMinor)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {settings.showNotes && invoice.notes && (
        <>
          <hr className="th-divider" />
          <div style={{ fontSize: 9 }}><b>Nota:</b> {invoice.notes}</div>
        </>
      )}

      {settings.showSignatureLine && (
        <>
          <hr className="th-divider" />
          <div style={{ fontSize: 9 }}>
            <div style={{ marginTop: 32, borderTop: '1px solid #000', paddingTop: 3 }}>Firma del cliente</div>
          </div>
        </>
      )}

      <hr className="th-divider" />
      <div className="th-center" style={{ fontSize: 9 }}>{settings.footerText}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   SETTINGS PANEL
═══════════════════════════════════════════════════════════ */
const SettingsPanel: React.FC<{
  settings: PrintSettings;
  onChange: (s: PrintSettings) => void;
  onClose: () => void;
}> = ({ settings, onChange, onClose }) => (
  <div className="w-72 shrink-0 bg-white border-l border-slate-200 flex flex-col h-full overflow-y-auto">
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
      <div className="flex items-center gap-2">
        <Cog6ToothIcon className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">Configurar plantilla</span>
      </div>
      <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
    <div className="p-4 flex-1">
      <InvoicePrintSettingsForm settings={settings} onChange={onChange} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
const InvoicePrintPreview: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const previewRef = useRef<HTMLDivElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const skipNextPersist = useRef(true);

  const [settings, setSettings] = useState<PrintSettings>(() => {
    return loadInvoicePrintSettingsFromLocalStorage() || { ...DEFAULT_SETTINGS };
  });

  const { data: invoiceRes, isLoading: invoiceLoading } = useQuery(
    ['invoice-print', id],
    () => invoicesAPI.getById(id),
    { enabled: Boolean(id) }
  );

  const { data: configRes, isLoading: configLoading } = useQuery('config-print', configurationAPI.get, {
    staleTime: 5 * 60_000,
  });

  const invoice = (invoiceRes as any)?.data?.data;
  const config = (configRes as any)?.data?.data || (configRes as any)?.data || {};

  const saveSettingsMutation = useMutation(
    (next: PrintSettings) => configurationAPI.update({ invoicePrintSettings: next }),
    {
      onSuccess: (response) => {
        queryClient.setQueryData('config-print', response);
        queryClient.invalidateQueries('configuration');
      },
    }
  );

  // Hydrate from server (or migrate localStorage → server once)
  useEffect(() => {
    if (configLoading || settingsHydrated) return;
    const serverRaw = config?.invoicePrintSettings;
    if (serverRaw) {
      const normalized = normalizeInvoicePrintSettings(serverRaw);
      skipNextPersist.current = true;
      setSettings(normalized);
      saveInvoicePrintSettingsToLocalStorage(normalized);
    } else {
      const local = loadInvoicePrintSettingsFromLocalStorage();
      if (local) {
        skipNextPersist.current = true;
        setSettings(local);
        saveSettingsMutation.mutate(local);
      }
    }
    setSettingsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading, config?.invoicePrintSettings, settingsHydrated]);

  // Persist locally always; debounce API save after user edits
  useEffect(() => {
    saveInvoicePrintSettingsToLocalStorage(settings);
    if (!settingsHydrated) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      saveSettingsMutation.mutate(settings);
    }, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, settingsHydrated]);

  const handlePrint = () => {
    window.print();
  };

  const handleResetSettings = () => {
    setSettings({ ...DEFAULT_SETTINGS });
  };

  if (invoiceLoading || !invoice) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const TemplateComponent =
    settings.templateId === 'thermal' ? TemplateThermal
    : settings.templateId === 'compact' ? TemplateCompact
    : TemplateFormal;

  return (
    <div className="flex flex-col h-screen bg-slate-100">
      {/* Top toolbar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-slate-200 shrink-0 print:hidden">
        <button
          type="button"
          onClick={() => navigate(`/invoices/${id}`)}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Volver
        </button>

        <div className="w-px h-5 bg-slate-200" />

        <span className="text-sm font-semibold text-slate-800">
          Vista previa — {invoice.invoiceNumber || `Factura #${invoice.id}`}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetSettings}
            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            Restablecer
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all ${
              settingsOpen
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            <Cog6ToothIcon className="h-4 w-4" />
            Configurar
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <PrinterIcon className="h-4 w-4" />
            Imprimir
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden print:overflow-visible">
        {/* Preview canvas */}
        <div className="flex-1 overflow-auto flex justify-center py-8 px-4 print:p-0 print:overflow-visible">
          <div
            ref={previewRef}
            className="shadow-xl print:shadow-none"
            style={{
              background: '#fff',
              transform: settings.templateId === 'thermal' ? 'none' : undefined,
            }}
          >
            <TemplateComponent invoice={invoice} config={config} settings={settings} />
          </div>
        </div>

        {/* Settings panel */}
        {settingsOpen && (
          <div className="print:hidden h-full overflow-y-auto border-l border-slate-200">
            <SettingsPanel
              settings={settings}
              onChange={setSettings}
              onClose={() => setSettingsOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePrintPreview;
